import { useEffect, useRef, useState } from "react";
// Figma specifies Iconly/Light/Arrow at 24x24 for this control (node 38304-44707). No
// asset in the repo carries that name; date-arrow-right.svg is the closest match in the
// same drawing style - stroked shaft plus head, stroke-width 1.46, round caps - and is
// already the right green (#178477).
//
// ONE import, mirrored with CSS for the left arrow. Its partner date-arrow-left.svg is
// stroke="black", so using the pair as shipped would put a black arrow opposite a green
// one. Mirroring guarantees the two match and removes the second file to keep in step.
import DateArrow from "../../../assets/images/icons/date-arrow-right.svg";
import { isBeyondNavigationEnd } from "./calendarBookingUtils";

// Breathing room between the bottom of the month toolbar and the top of the popup.
const TOOLBAR_GAP_PX = 8;

const MonthJumpSelector = ({
    isOpen,
    year,
    selectedMonthDate,
    minimumMonthDate,
    navigationEndDate,
    locale,
    onClose,
    onPreviousYear,
    onNextYear,
    onSelectMonth,
}) => {
    const selectorRef = useRef(null);

    // Viewport coordinates for the popup, measured from the month toolbar when it opens.
    //
    // It used to be `position: absolute; top: 72px` inside `.monthly-view-sec`, i.e.
    // anchored 72px below the top of the month SECTION - which scrolls away. The toolbar
    // is sticky so the button stays put, but the popup did not follow it: scrolled down,
    // it rendered above the visible area and the scroll container clipped its year row
    // off. Fixed positioning also escapes that container's overflow entirely.
    const [anchorStyle, setAnchorStyle] = useState(null);
    const safeMinimumMonth = new Date(
        minimumMonthDate.getFullYear(),
        minimumMonthDate.getMonth(),
        1
    );

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const positionToToolbar = () => {
            // Scoped to the month view so the week view's toolbar can never be picked up.
            const toolbar = document.querySelector(".monthly-view-sec .fc-header-toolbar");

            if (!toolbar) {
                return;
            }

            const rect = toolbar.getBoundingClientRect();

            setAnchorStyle({
                top: `${rect.bottom + TOOLBAR_GAP_PX}px`,
                // Centred on the TOOLBAR, not the viewport: the sidebar makes the calendar
                // area off-centre, and it collapses, so a viewport-centred popup would sit
                // wrong and move when the sidebar toggles.
                left: `${rect.left + rect.width / 2}px`,
            });
        };

        positionToToolbar();

        // The toolbar rests exactly where it sticks, so scrolling does not move it and no
        // scroll listener is needed. A resize does move it.
        window.addEventListener("resize", positionToToolbar);

        const handlePointerDown = (event) => {
            if (!selectorRef.current?.contains(event.target)) {
                onClose();
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("resize", positionToToolbar);
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, onClose]);

    if (!isOpen) {
        return null;
    }

    const selectedYear = selectedMonthDate.getFullYear();
    const selectedMonth = selectedMonthDate.getMonth();
    const isPreviousYearDisabled = year <= safeMinimumMonth.getFullYear();
    // Nothing in the following year is reachable, so there is no point paging to it.
    // `navigationEndDate` is exclusive, so January of year+1 being at or past it means
    // the whole of that year is out.
    const isNextYearDisabled = isBeyondNavigationEnd(new Date(year + 1, 0, 1), navigationEndDate);

    return (
        <div
            ref={selectorRef}
            className="month-jump-selector"
            style={anchorStyle || undefined}
            role="dialog"
            aria-label="Select month"
        >
            <div className="month-jump-selector__year">
                <button
                    type="button"
                    className="month-jump-selector__year-button"
                    onClick={onPreviousYear}
                    disabled={isPreviousYearDisabled}
                    aria-label="Previous year"
                >
                    <img
                        src={DateArrow}
                        alt=""
                        aria-hidden="true"
                        className="month-jump-selector__year-icon month-jump-selector__year-icon--left"
                    />
                </button>
                <strong>{year}</strong>
                <button
                    type="button"
                    className="month-jump-selector__year-button"
                    onClick={onNextYear}
                    disabled={isNextYearDisabled}
                    aria-label="Next year"
                >
                    <img
                        src={DateArrow}
                        alt=""
                        aria-hidden="true"
                        className="month-jump-selector__year-icon"
                    />
                </button>
            </div>
            <div className="month-jump-selector__months">
                {Array.from({ length: 12 }, (_, monthIndex) => {
                    const targetDate = new Date(year, monthIndex, 1);
                    // All twelve months always render. Out-of-range ones are greyed, never
                    // hidden: a full, stable grid keeps each month in the same position
                    // whichever year you are on, and a greyed cell says "not available"
                    // where a missing one just looks like a rendering fault.
                    //
                    // Bounded at BOTH ends. Without the upper bound the picker offered
                    // months the calendar cannot navigate to - navigateToMonthDate checks
                    // the same cap and silently returns, so the click did nothing. It is the
                    // cap `validRange` uses for the toolbar's next button, so the two
                    // controls agree.
                    const isDisabled =
                        targetDate < safeMinimumMonth ||
                        isBeyondNavigationEnd(targetDate, navigationEndDate);
                    const isSelected = year === selectedYear && monthIndex === selectedMonth;
                    const label = new Intl.DateTimeFormat(locale || "en", { month: "short" })
                        .format(targetDate)
                        .toUpperCase();

                    return (
                        <button
                            key={monthIndex}
                            type="button"
                            className={`month-jump-selector__month${isSelected ? " is-selected" : ""}`}
                            disabled={isDisabled}
                            onClick={() => onSelectMonth(targetDate)}
                        >
                            {label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default MonthJumpSelector;
